import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { PENDING_BALANCES_QUERY_KEY } from "./constants";
import type { PayResponse } from "./types";

interface PayVenueParams {
  readonly venueId: string;
  /** Optional justification (≤500 chars); blank is omitted from the request. */
  readonly reason?: string;
}

/**
 * Hand-written fetch — see CLAUDE.md → TD-07. `POST …/venues/:id/pay`
 * (API-FIN-2, WANDERFIT_ADMIN only, throttled 5/min, audited) settles ALL of
 * a venue's pending currencies at once. Idempotent server-side (Stripe key =
 * sha256 of the accrual set), so a double-submit won't double-pay.
 */
async function payVenue({ venueId, reason }: PayVenueParams): Promise<PayResponse> {
  const trimmed = reason?.trim();
  return httpClient<PayResponse>(`/api/admin/payouts/venues/${encodeURIComponent(venueId)}/pay`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(trimmed ? { reason: trimmed } : {}),
  });
}

export function usePayVenue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: payVenue,
    onSuccess: () => {
      // Paid currencies drop off the pending report — refetch so the table
      // and its balances stay accurate.
      queryClient.invalidateQueries({ queryKey: PENDING_BALANCES_QUERY_KEY });
    },
  });
}
